if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', class CartCrossSell extends HTMLElement {
    constructor() {
      super();
      this.listName = 'cart_cross_sell';
      this.renderToken = 0;
      this.visibleTracked = false;
      this.pendingAdd = null;
    }

    connectedCallback() {
      this.init().catch((error) => {
        console.error('[Cart cross-sell]', error);
        this.hide();
      });
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.visibilityObserver?.disconnect();
      if (this.documentSubmitHandler) document.removeEventListener('submit', this.documentSubmitHandler, true);
      document.querySelectorAll(`[data-cart-cross-sell-modal-owner="${this.dataset.sectionId}"]`).forEach((modal) => modal.remove());
    }

    readJson(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));
      } catch (_) {
        return fallback;
      }
    }

    async init() {
      this.cartProducts = this.readJson('[data-cart-cross-sell-cart]', []).filter((product) => product?.id);
      this.boxExclusions = new Set(this.readJson('[data-cart-cross-sell-box-exclusions]', []).map((id) => window.PerroneCrossSellEngine.normalizeId(id)));
      this.catalog = new Map(this.readJson('[data-cart-cross-sell-catalog]', []).map((product) => [window.PerroneCrossSellEngine.normalizeId(product.id), product]));
      if (!this.cartProducts.length) return this.hide();

      const response = await fetch(this.dataset.configUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Configuration request failed (${response.status})`);
      this.config = await response.json();
      this.bindControls();
      this.bindTracking();
      await this.renderRecommendations();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'cart-cross-sell-refresh') return;
          if (this.pendingAdd && event?.source === 'product-form') {
            this.track('add_to_cart', [this.pendingAdd]);
            this.pendingAdd = null;
          }
          window.setTimeout(() => this.refreshSection(), 30);
        });
      }
    }

    bindControls() {
      this.scroller = this.querySelector('[data-cart-cross-sell-list]');
      this.prevButton = this.querySelector('[data-cart-cross-sell-prev]');
      this.nextButton = this.querySelector('[data-cart-cross-sell-next]');
      const move = (direction) => this.scroller?.scrollBy({ left: this.scrollAmount() * direction, behavior: 'smooth' });
      this.prevButton?.addEventListener('click', () => move(-1));
      this.nextButton?.addEventListener('click', () => move(1));
      this.scroller?.addEventListener('scroll', () => this.requestButtonUpdate(), { passive: true });
      window.addEventListener('resize', () => this.requestButtonUpdate(), { passive: true });
    }

    bindTracking() {
      this.addEventListener('click', (event) => {
        const item = event.target.closest('[data-cross-sell-item]');
        if (!item) return;
        const index = Number(item.dataset.index);
        const product = this.currentProducts?.[index];
        if (!product) return;
        if (event.target.closest('a, modal-opener, .quick-add__submit')) {
          this.track('select_item', [this.analyticsItem(product, index)]);
        }
        if (event.target.closest('modal-opener')) this.activeSelection = this.analyticsItem(product, index);
      });

      this.addEventListener('submit', (event) => {
        const item = event.target.closest('[data-cross-sell-item]');
        if (!item) return;
        const index = Number(item.dataset.index);
        const product = this.currentProducts?.[index];
        if (product) this.pendingAdd = this.analyticsItem(product, index, event.target.querySelector('[name="id"]')?.value);
      }, true);

      this.documentSubmitHandler = (event) => {
        if (!this.activeSelection || !event.target.closest('quick-add-modal')) return;
        this.pendingAdd = {
          ...this.activeSelection,
          variant_id: String(event.target.querySelector('[name="id"]')?.value || this.activeSelection.variant_id)
        };
        this.activeSelection = null;
      };
      document.addEventListener('submit', this.documentSubmitHandler, true);
    }

    recommendations() {
      return window.PerroneCrossSellEngine.recommend({
        config: this.config,
        sourceProducts: this.cartProducts,
        excludedIds: [...this.boxExclusions],
        catalog: this.catalog,
        country: this.dataset.country,
        subtotal: Number(this.dataset.cartSubtotal || 0),
        mode: 'cart',
        limitOverride: this.catalog.size
      });
    }

    async renderRecommendations() {
      const token = ++this.renderToken;
      const recommendations = this.recommendations();
      if (!recommendations.length) return this.hide();
      const selected = await window.PerroneCrossSellEngine.loadCards({
        candidates: recommendations, limit: 4, cardSection: this.dataset.cardSection
      });
      const rendered = selected.map(({ candidate, card }, index) => {
        card.querySelectorAll('.quick-add__submit').forEach((button) => {
          const labelSpan = button.querySelector(':scope > span:not(.sold-out-message):not(.loading__spinner)');
          if (labelSpan) labelSpan.textContent = this.dataset.addLabel;
          else [...button.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()).forEach((node) => { node.textContent = ` ${this.dataset.addLabel} `; });
        });
        const item = document.createElement('li');
        item.className = 'grid__item';
        item.dataset.crossSellItem = candidate.id;
        item.dataset.index = String(index);
        item.innerHTML = card.innerHTML;
        item.querySelectorAll('quick-add-modal').forEach((modal) => {
          modal.dataset.cartCrossSellModalOwner = this.dataset.sectionId;
        });
        return item;
      });
      if (token !== this.renderToken) return;
      this.currentProducts = selected.map(({ candidate }) => candidate.product);
      this.scroller.replaceChildren(...rendered.filter(Boolean));
      if (!this.scroller.children.length) return this.hide();
      this.querySelector('[data-cart-cross-sell-content]').hidden = false;
      this.visibleTracked = false;
      this.observeVisibility();
      this.requestButtonUpdate();
    }

    observeVisibility() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!this.visibleTracked && entries.some((entry) => entry.isIntersecting)) {
          this.visibleTracked = true;
          this.track('view_item_list', this.currentProducts.map((product, index) => this.analyticsItem(product, index)));
          this.visibilityObserver.disconnect();
        }
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this);
    }

    analyticsItem(product, index, variantId) {
      return {
        item_id: String(product.id),
        product_id: String(product.id),
        variant_id: String(variantId || product.variantId || ''),
        item_name: product.title,
        price: Number(product.price || 0) / 100,
        quantity: 1,
        index: index + 1,
        item_list_name: this.listName
      };
    }

    track(eventName, items) {
      const payload = {
        item_list_name: this.listName,
        items,
        cart_item_count: this.cartProducts.length,
        cart_subtotal_before: Number(this.dataset.cartSubtotal || 0) / 100
      };
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
      else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ecommerce: payload });
      }
      document.dispatchEvent(new CustomEvent('cart-cross-sell:analytics', { detail: { eventName, payload } }));
    }

    scrollAmount() {
      const item = this.scroller?.querySelector('.grid__item');
      const styles = this.scroller ? getComputedStyle(this.scroller) : null;
      return item ? item.getBoundingClientRect().width + (parseFloat(styles.columnGap || styles.gap || 0) || 0) : this.scroller?.clientWidth || 0;
    }

    requestButtonUpdate() {
      cancelAnimationFrame(this.buttonFrame);
      this.buttonFrame = requestAnimationFrame(() => {
        if (!this.scroller || !this.prevButton || !this.nextButton) return;
        const max = Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
        this.prevButton.disabled = max < 4 || this.scroller.scrollLeft <= 4;
        this.nextButton.disabled = max < 4 || this.scroller.scrollLeft >= max - 4;
      });
    }

    async refreshSection() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId || this.refreshing) return;
      this.refreshing = true;
      try {
        const footer = document.getElementById('main-cart-footer');
        const shipping = document.querySelector('[data-ct-cart-free-shipping-bar]');
        const ids = [sectionId, footer?.dataset.id, shipping?.dataset.id].filter(Boolean);
        const response = await fetch(`${window.routes.cart_url}?sections=${ids.map(encodeURIComponent).join(',')}`, { credentials: 'same-origin' });
        const sections = await response.json();
        const html = new DOMParser().parseFromString(sections[sectionId] || '', 'text/html');
        const fresh = html.querySelector('cart-cross-sell');
        if (footer?.dataset.id && sections[footer.dataset.id]) {
          const parsedFooter = new DOMParser().parseFromString(sections[footer.dataset.id], 'text/html');
          const currentContents = footer.querySelector('.js-contents');
          const freshContents = parsedFooter.querySelector('#main-cart-footer .js-contents');
          if (currentContents && freshContents) currentContents.innerHTML = freshContents.innerHTML;
        }
        if (shipping?.dataset.id && sections[shipping.dataset.id]) {
          const parsedShipping = new DOMParser().parseFromString(sections[shipping.dataset.id], 'text/html');
          const currentContents = shipping.querySelector('.js-contents');
          const freshContents = parsedShipping.querySelector('.js-contents');
          if (currentContents && freshContents) currentContents.innerHTML = freshContents.innerHTML;
        }
        if (fresh && this.isConnected) this.replaceWith(fresh);
      } catch (error) {
        console.error('[Cart cross-sell refresh]', error);
      } finally {
        this.refreshing = false;
      }
    }

    hide() {
      this.classList.add('is-empty');
      this.querySelector('[data-cart-cross-sell-content]')?.setAttribute('hidden', '');
    }
  });
}

